import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import type { FormikHelpers } from 'formik';
import { HotjarEvents, submitEvent } from 'hotjar';
import type { AddBankAccountModel } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';
import { npsService } from 'v2/services/nps';
import uploadService from 'v2/services/upload';

import { Breadcrumb } from './Breadcrumb';
import { Stepper } from './Stepper';
import { CAIXA_ECONOMICA_ID, PersonalDocumentType } from './Stepper/form';
import type { NewBankAccount } from './Stepper/form';

type VerifyProps = {
  accountBankId: number;
  verificationCode: string;
  setSubmitting: (isSubmitting: boolean) => void;
  setStatus: (status?: string) => void;
};

const NEW_BANK_ACCOUNT_SURVEY_ID = 958174;
const NEW_BANK_ACCOUNT_SURVEY_EXPIRATION_DAYS = 90;

export default function AddBankAccount() {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);

  const setHotjarAddBankAccountSurvey = React.useCallback(async () => {
    const isWhiteBelt = !user.is_elite;
    if (!isWhiteBelt) return;
    const lastAnswer = await npsService.getSurveyLastAnswer(NEW_BANK_ACCOUNT_SURVEY_ID);
    if (!lastAnswer) return;
    const isSurveyExpired = lastAnswer.answered && lastAnswer.days_count >= NEW_BANK_ACCOUNT_SURVEY_EXPIRATION_DAYS;
    const shouldShowSurvey = !lastAnswer.answered || isSurveyExpired;
    if (!shouldShowSurvey) return;
    submitEvent(HotjarEvents.NEW_BANK_ACCOUNT_CREATED);
  }, [user]);

  const uploadImage = React.useCallback(async (image: File) => {
    const upload = await uploadService.uploadFile(image, 'mpg_docs');
    return upload.data.upload_id;
  }, []);

  const verifyBankAccount = React.useCallback(
    async ({ accountBankId, verificationCode, setSubmitting, setStatus }: VerifyProps) => {
      try {
        setSubmitting(true);
        await financialService.verifyBankAccount(accountBankId, verificationCode);
        sessionStorage.removeItem('form_submitted');
        setStatus('verified');
        setHotjarAddBankAccountSurvey();
      } catch (err) {
        toast.error(err);
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
    [setHotjarAddBankAccountSurvey]
  );

  const handleSubmit = React.useCallback(
    async (values: NewBankAccount, { setSubmitting, setStatus, setFieldValue }: FormikHelpers<NewBankAccount>) => {
      if (!sessionStorage.form_submitted) {
        const getRequest = (formData: AddBankAccountModel) => {
          if (values?.id) return financialService.editBankAccount(formData);
          return financialService.addBankAccount(formData);
        };

        try {
          setSubmitting(true);
          const {
            bank_receipt_image,
            selfie_video,
            document_image_type,
            document_image_front,
            document_image_back,
            ...rest
          } = values;

          const model = { ...rest } as AddBankAccountModel;

          const savedFiles = sessionStorage?.inputFiles && JSON.parse(sessionStorage.inputFiles);

          const savedDocumentFront = savedFiles?.document_image_front;
          const savedDocumentBack = savedFiles?.document_image_back;
          const savedAddressImage = savedFiles?.address_image;
          const savedDocumentVideo = savedFiles?.personal_document_video;

          const noSavedDocuments = !savedDocumentFront && !savedDocumentBack;
          const noNewDocuments = !document_image_front && !document_image_back;
          const needDocuments = noNewDocuments && noSavedDocuments;
          const needAddressImage = !bank_receipt_image && !savedAddressImage;
          const needVideo = !selfie_video && !savedDocumentVideo;

          if (needDocuments) throw 'Por favor, nos envie alguma foto de documento!';
          if (needVideo) throw 'Por favor, nos envie um vídeo de validação!';
          if (needAddressImage) throw 'Por favor, nos envie um comprovante bancário!';

          if (savedDocumentFront) {
            switch (document_image_type) {
              case PersonalDocumentType.RG:
                model.personal_document_image_front = savedDocumentFront;
                break;

              case PersonalDocumentType.CNH:
                model.driver_license_image = savedDocumentFront;
                break;

              default:
                model.professional_identity_image_front = savedDocumentFront;
                break;
            }
          }

          if (savedDocumentBack) {
            switch (document_image_type) {
              case PersonalDocumentType.RG:
                model.personal_document_image_back = savedDocumentBack;
                break;

              default:
                model.professional_identity_image_back = savedDocumentBack;
                break;
            }
          }

          if (savedAddressImage) model.address_image = savedAddressImage;
          if (savedDocumentVideo) model.personal_document_video = savedDocumentVideo;

          if (document_image_type === PersonalDocumentType.RG && document_image_front && document_image_back) {
            const [frontId, backId] = await Promise.all([
              uploadImage(document_image_front),
              uploadImage(document_image_back)
            ]);
            model.personal_document_image_front = frontId;
            model.personal_document_image_back = backId;
          }

          if (document_image_type === PersonalDocumentType.CNH && document_image_front) {
            model.driver_license_image = await uploadImage(document_image_front);
          }

          if (
            document_image_type === PersonalDocumentType.PROFESSIONAL &&
            document_image_front &&
            document_image_back
          ) {
            const [frontId, backId] = await Promise.all([
              uploadImage(document_image_front),
              uploadImage(document_image_back)
            ]);
            model.professional_identity_image_front = frontId;
            model.professional_identity_image_back = backId;
          }

          if (bank_receipt_image) {
            model.address_image = await uploadImage(bank_receipt_image);
          }

          if (selfie_video) {
            model.personal_document_video = await uploadImage(selfie_video);
          }

          if (values.bank_id !== CAIXA_ECONOMICA_ID) {
            delete model.operation_id;
          }

          Object.keys(model).forEach((key: keyof AddBankAccountModel) => {
            if (model[key] === null) {
              delete model[key];
            }
          });

          await getRequest(model).then(data => setFieldValue('id', data.fund_transfer.id));

          sessionStorage.setItem('form_submitted', 'true');
          sessionStorage.removeItem('inputFiles');
          setStatus('submitted');
        } catch (err) {
          toast.error(err);
          console.error(err);
        } finally {
          setSubmitting(false);
        }

        return;
      }

      if (!!sessionStorage.form_submitted) {
        verifyBankAccount({ accountBankId: values.id, verificationCode: values.verify_code, setSubmitting, setStatus });
      }
    },
    [uploadImage, verifyBankAccount]
  );

  React.useEffect(() => {
    return () => {
      sessionStorage.removeItem('form_submitted');
      sessionStorage.removeItem('inputFiles');
    };
  }, []);

  if (!betaModules) return null;

  return (
    <div>
      <Breadcrumb />
      <Stepper handleSubmit={handleSubmit} />
    </div>
  );
}
